/**
 * Browser renderer for resolved chat blocks (plan 2 §11.2.6).
 * Unknown types become a code block. Nothing here calls a resolver.
 */
import { el } from '../../core/dom.js';
import { renderBarList } from './bar-list.js';
import { renderBreakdown } from './breakdown.js';
import { renderCallout } from './callout.js';
import { renderChart } from './chart.js';
import { renderCohort } from './cohort.js';
import { renderCompare } from './compare.js';
import { renderFollowups } from './followups.js';
import { renderKpi } from './kpi.js';
import { renderActionPlan } from './action-plan.js';
import { renderReportHeader } from './report-header.js';
import { renderSuggestAnalysis } from './suggest-analysis.js';
import { renderRun } from './run.js';
import { renderRetry } from './retry.js';
import { BLOCK_TYPES, freshnessText, resolvedOf, stripBlockFences } from './shape.js';
import { extractRunRetryBlocks } from './run-state.js';
import { renderTable } from './table.js';

export { stripBlockFences, BLOCK_TYPES };
export { extractRunRetryBlocks };
export { disposeBlockCharts, paintBlockCharts } from './chart.js';
export { csvMatrix, toCsv } from './csv.js';
export { chartAriaLabel, chartBlockOption } from './chart-option.js';

const RENDERERS = {
  kpi: renderKpi,
  chart: renderChart,
  table: renderTable,
  'bar-list': renderBarList,
  cohort: renderCohort,
  'breakdown-grid': renderBreakdown,
  compare: renderCompare,
  callout: renderCallout,
  followups: renderFollowups,
  // S46b report blocks
  'report-header': renderReportHeader,
  'action-plan': renderActionPlan,
  // S59c suggestion card
  'suggest-analysis': renderSuggestAnalysis,
  // S60b run panel + retry
  run: renderRun,
  retry: renderRetry,
};

function renderUnknown(block) {
  let raw = '';
  try {
    raw = JSON.stringify(resolvedOf(block), null, 2);
  } catch {
    raw = String(block && block.type ? block.type : 'block');
  }
  return el('pre', {
    class: 'chat-block chat-block-unknown',
    'data-block-type': (block && block.type) || 'unknown',
    'aria-label': 'Unsupported block',
  }, el('code', {}, raw));
}

/**
 * @param {object[]} blocks
 * @param {{ onAsk?: Function }} [actions]
 * @returns {HTMLElement}
 */
export function renderBlocks(blocks, actions) {
  const list = el('div', { class: 'chat-blocks' });
  (Array.isArray(blocks) ? blocks : []).forEach((block) => {
    if (!block || typeof block !== 'object') return;
    const render = RENDERERS[block.type] || renderUnknown;
    const node = render(block, actions || {});
    const fresh = freshnessText(block.source && block.source.freshness);
    if (fresh) node.append(el('p', { class: 'chat-fresh' }, fresh));
    list.append(node);
  });
  return list;
}
